import React, { useEffect, useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { Link } from 'react-router-dom';
import { provider, ethersUtils, createContract, getAccounts, initializeEthers } from './EthersSetup';
import DICY from '../deployedContractsInfo/DICY.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import useBlockchainEvents from '../hooks/useBlockchainEvents';
import { AddressDisplayLong } from './AddressDisplay.js';

const DICYAdmin = () => {
  const [contractState, setContractState] = useState({
    totalBank: 0,
    totalShares: 0,
    isPaused: false,
    deployerAddress: '',
    tokenHoldersCount: 0
  });

  const [userState, setUserState] = useState({
    userAddress: '',
    isDeployer: false
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [pendingTx, setPendingTx] = useState(false);
  const [showKillConfirmation, setShowKillConfirmation] = useState(false);

  // Create stable contract instances
  const [dicyContract, setDicyContract] = useState(null);
  const [ethersContract, setEthersContract] = useState(null);

  // Load contract data function
  const loadContractData = useCallback(async (isInitialLoad = false) => {
    if (!dicyContract) return;
    try {
      if (isInitialLoad) {
        setLoading(true);
      }

      // Get contract information
      const bankValue = await dicyContract.getBankValue();
      const totalSupply = await dicyContract.totalSupply();
      const deployer = await dicyContract.deployer();
      const paused = await dicyContract.paused();

      // Try to get token holders count (may fail on older contracts)
      let holdersCount = 0;
      try {
        holdersCount = await dicyContract.getTokenHoldersCount();
      } catch (error) {
        console.warn('Token holders count not available:', error.message);
      }

      setContractState({
        totalBank: bankValue,
        totalShares: totalSupply,
        isPaused: paused,
        deployerAddress: deployer,
        tokenHoldersCount: holdersCount
      });

      // Check if current user is deployer
      if (userState.userAddress) {
        setUserState(prev => ({
          ...prev,
          isDeployer: prev.userAddress.toLowerCase() === deployer.toLowerCase()
        }));
      }

      if (isInitialLoad) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to load contract data:', error);
      setErrorMessage('Failed to load DICY admin data');
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, [dicyContract, userState.userAddress]);

  // Initialize wallet first
  useEffect(() => {
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserState(prev => ({ ...prev, userAddress: accounts[0] }));
        }
        setIsWalletInitialized(true);
      } catch (error) {
        console.error('DICY Admin: Failed to initialize wallet:', error);
        setIsWalletInitialized(true); // Still allow component to continue
      }
    };
    initWallet();
  }, []);

  // Set browser tab title for the admin page
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `DICY Admin`;

    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Initialize contract and load initial data
  useEffect(() => {
    if (!isWalletInitialized) return;

    const initializeContract = async () => {
      try {
        const contract = await createContract(contractAddresses.DICY, DICY.abi);
        setDicyContract(contract);

        // Create ethers contract for events
        if (provider) {
          const ethersContract = new ethers.Contract(contractAddresses.DICY, DICY.abi, provider);
          setEthersContract(ethersContract);
          console.log('🔧 DICY Admin: Ethers contract created');
        } else {
          console.log('🔧 DICY Admin: No provider available for ethers contract');
        }
      } catch (error) {
        console.error('Error creating DICY contract:', error);
        setDicyContract(null);
        setEthersContract(null);
      }
    };

    initializeContract();
  }, [isWalletInitialized]);

  // Load data when contract is ready
  useEffect(() => {
    if (dicyContract) {
      loadContractData(true); // Initial load - show loading state
    }
  }, [dicyContract, loadContractData]);

  // Event handler for updates
  const handleContractUpdate = useCallback(async (eventName) => {
    await loadContractData(false); // Event-driven update - no loading state change
  }, [loadContractData]);

  // Set up blockchain event listening
  const eventState = useBlockchainEvents({
    contract: ethersContract,
    gameId: null, // No specific game ID for DICY
    events: [
      {
        name: 'ContractKilled',
        filter: [],
        handler: () => handleContractUpdate('ContractKilled')
      },
      {
        name: 'Paused',
        filter: [],
        handler: () => handleContractUpdate('Paused')
      },
      {
        name: 'Unpaused',
        filter: [],
        handler: () => handleContractUpdate('Unpaused')
      },
      {
        name: 'InvestorDeposit',
        filter: [],
        handler: () => handleContractUpdate('InvestorDeposit')
      },
      {
        name: 'InvestorWithdraw',
        filter: [],
        handler: () => handleContractUpdate('InvestorWithdraw')
      }
    ],
    enabled: !loading && ethersContract !== null,
    fallbackInterval: 3000 // 3 second fallback polling
  });

  // Pause contract (deployer only)
  const handlePause = async () => {
    if (!dicyContract) return;

    try {
      setPendingTx(true);
      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.pauseContract();
      await tx.wait();
      setPendingTx(false);
    } catch (error) {
      console.error('Pause failed:', error);
      setErrorMessage('Pause failed: ' + error.message);
      setPendingTx(false);
    }
  };

  // Unpause contract (deployer only)
  const handleUnpause = async () => {
    if (!dicyContract) return;

    try {
      setPendingTx(true);
      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.unpauseContract();
      await tx.wait();
      setPendingTx(false);
    } catch (error) {
      console.error('Unpause failed:', error);
      setErrorMessage('Unpause failed: ' + error.message);
      setPendingTx(false);
    }
  };

  // Kill contract (deployer only)
  const handleKillContract = async () => {
    if (!dicyContract) return;

    try {
      setPendingTx(true);
      setShowKillConfirmation(false);
      // Create signed contract only when needed for transaction
      const contractWithSigner = await createContract(contractAddresses.DICY, DICY.abi, true);
      const tx = await contractWithSigner.killContract();
      await tx.wait();
      setPendingTx(false);
    } catch (error) {
      console.error('Kill contract failed:', error);
      setErrorMessage('Kill contract failed: ' + error.message);
      setPendingTx(false);
    }
  };

  const formatEth = (wei) => {
    const ethValue = ethersUtils.fromWei(wei.toString(), 'ether');
    return formatWithSeparators(ethValue);
  };

  // Helper function to format numbers with Swiss thousand separators
  const formatWithSeparators = (value) => {
    if (!value || value === '0') return value;
    try {
      const numStr = typeof value === 'string' ? value : value.toString();
      const parts = numStr.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return parts.join('.');
    } catch (error) {
      return value;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  if (!userState.isDeployer) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h1>
        <p className="text-gray-600 mb-4">This admin panel is only accessible to the contract deployer.</p>
        <p className="text-sm text-gray-500 mb-4">
          Current address: <AddressDisplayLong address={userState.userAddress} />
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Deployer address: <AddressDisplayLong address={contractState.deployerAddress} />
        </p>
        <Link
          to="/asymmetric/DICY"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Go to DICY Game
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Error Banner */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Error: {errorMessage}
            <button
              onClick={() => setErrorMessage('')}
              className="ml-4 bg-red-600 text-white px-3 py-1 rounded text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        <h1 className="text-4xl font-bold text-center mb-4 text-gray-800">
          DICY Administration
        </h1>

        <p className="text-center text-gray-600 mb-8">
          Contract management interface for deployer only. <Link to="/asymmetric/DICY" className="text-blue-600 hover:text-blue-800 underline">Back to DICY Game</Link>
        </p>

        {/* Contract Status Overview */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Contract Status</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 p-4 rounded">
              <p className="text-sm text-gray-600">Contract Status</p>
              <p className={`text-lg font-semibold ${contractState.isPaused ? 'text-red-600' : 'text-green-600'}`}>
                {contractState.isPaused ? 'PAUSED' : 'ACTIVE'}
              </p>
            </div>

            <div className="bg-green-50 p-4 rounded">
              <p className="text-sm text-gray-600">Total Bank</p>
              <p className="text-lg font-semibold">{formatEth(contractState.totalBank)} POL</p>
            </div>

            <div className="bg-purple-50 p-4 rounded">
              <p className="text-sm text-gray-600">Total Shares</p>
              <p className="text-lg font-semibold">{formatWithSeparators(contractState.totalShares.toString())}</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded">
              <p className="text-sm text-gray-600">Token Holders</p>
              <p className="text-lg font-semibold">{contractState.tokenHoldersCount}</p>
            </div>
          </div>

          <div className="mt-4 bg-gray-50 p-4 rounded">
            <p className="text-sm text-gray-600">Deployer Address</p>
            <p className="font-mono text-sm"><AddressDisplayLong address={contractState.deployerAddress} /></p>
          </div>
        </div>

        {/* Contract Controls */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Contract Controls</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pause/Unpause Controls */}
            <div className="bg-orange-50 p-4 rounded">
              <h3 className="text-lg font-semibold mb-3 text-orange-800">Pause Controls</h3>
              <p className="text-sm text-gray-600 mb-4">
                Pausing stops new deposits and bets. Withdrawals remain available.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handlePause}
                  disabled={pendingTx || contractState.isPaused}
                  className={`px-4 py-2 rounded text-white font-semibold ${
                    contractState.isPaused || pendingTx
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                >
                  {pendingTx ? 'Processing...' : 'Pause Contract'}
                </button>

                <button
                  onClick={handleUnpause}
                  disabled={pendingTx || !contractState.isPaused}
                  className={`px-4 py-2 rounded text-white font-semibold ${
                    !contractState.isPaused || pendingTx
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                >
                  {pendingTx ? 'Processing...' : 'Unpause Contract'}
                </button>
              </div>
            </div>

            {/* Killswitch Controls */}
            <div className="bg-red-50 p-4 rounded">
              <h3 className="text-lg font-semibold mb-3 text-red-800">Emergency Killswitch</h3>
              <p className="text-sm text-gray-600 mb-4">
                ⚠️ Permanently pauses contract and distributes all funds proportionally to token holders. This action cannot be undone.
              </p>

              <button
                onClick={() => setShowKillConfirmation(true)}
                disabled={pendingTx || contractState.isPaused}
                className={`w-full px-4 py-2 rounded text-white font-semibold ${
                  contractState.isPaused || pendingTx
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {pendingTx ? 'Processing...' : 'Kill Contract & Distribute Funds'}
              </button>
            </div>
          </div>
        </div>

        {/* Information Panel */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Information</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Pause Function</h3>
              <p className="text-gray-600">
                Temporarily stops new deposits and bets. Existing token holders can still withdraw their shares.
                Can be reversed with unpause.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Killswitch Function</h3>
              <p className="text-gray-600">
                Permanently shuts down the contract by pausing all operations and automatically distributing
                the entire bank balance to token holders proportional to their ownership percentage. All tokens
                are burned in the process. This action is irreversible.
              </p>
            </div>
          </div>
        </div>

        {/* Kill Confirmation Modal */}
        {showKillConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-600 mb-4">⚠️ Confirm Contract Kill</h3>

              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  You are about to permanently kill the DICY contract. This will:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Permanently pause the contract</li>
                  <li>Distribute {formatEth(contractState.totalBank)} POL to {contractState.tokenHoldersCount} token holders</li>
                  <li>Burn all {formatWithSeparators(contractState.totalShares.toString())} tokens</li>
                  <li>Make the contract unusable forever</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-sm text-red-700 font-semibold">
                  This action cannot be undone!
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowKillConfirmation(false)}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleKillContract}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold"
                >
                  Kill Contract
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DICYAdmin;